import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Canvas as FabricCanvas, PencilBrush, Pattern } from 'fabric';
import { Button } from '@/components/ui/button';
import { Trash2, Download, Undo, Send, Brush, Image } from 'lucide-react';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { generateId } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { generateImage } from '@/lib/openai';
import ApiKeyDialog from './ApiKeyDialog';
import { useAppStore } from '@/store';

interface DrawingCanvasProps {
  onSave?: (imageData: string) => void;
  onClose?: () => void;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onSave, onClose }) => {
  const { darkMode } = useAppStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<FabricCanvas | null>(null);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushRadius, setBrushRadius] = useState(5);
  const [canvasHistory, setCanvasHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAiDialogOpen, setIsAiDialogOpen] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isApiKeyDialogOpen, setIsApiKeyDialogOpen] = useState(false);
  const { toast } = useToast();
  
  const hasInitializedRef = useRef(false);
  const initialSaveCompletedRef = useRef(false);
  const isStateUpdateAllowedRef = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const canvasBackground = useMemo(() => darkMode ? '#222222' : '#ffffff', [darkMode]);
  
  const colors = [
    '#000000', // Black
    '#ffffff', // White
    '#ff0000', // Red
    '#00ff00', // Green
    '#0000ff', // Blue
    '#ffff00', // Yellow
    '#ff00ff', // Magenta
    '#00ffff', // Cyan
    '#9b87f5', // Light Purple
    '#6E59A5', // Dark Purple
  ];

  const saveCanvasState = useCallback(() => {
    if (!fabricCanvasRef.current || !isCanvasReady || !isStateUpdateAllowedRef.current) return;
    
    if (saveTimeoutRef.current !== null) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    isStateUpdateAllowedRef.current = false;
    
    saveTimeoutRef.current = setTimeout(() => {
      try {
        if (!fabricCanvasRef.current) return;
        
        const canvasData = fabricCanvasRef.current.toDataURL({
          format: 'png',
          multiplier: 1
        });
        
        if (historyIndex >= 0 && canvasHistory.length > 0 && canvasData === canvasHistory[historyIndex]) {
          isStateUpdateAllowedRef.current = true;
          return;
        }
        
        setCanvasHistory(prev => {
          const newHistory = prev.slice(0, historyIndex + 1);
          newHistory.push(canvasData);
          return newHistory;
        });
        
        setHistoryIndex(prev => prev + 1);
      } catch (err) {
        console.error('Error saving canvas state:', err);
      } finally {
        isStateUpdateAllowedRef.current = true;
        saveTimeoutRef.current = null;
      }
    }, 500);
  }, [canvasHistory, historyIndex, isCanvasReady]);
  
  useEffect(() => {
    if (!canvasRef.current || !canvasContainerRef.current || hasInitializedRef.current) return;
    
    const container = canvasContainerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    try {
      const canvas = new FabricCanvas(canvasRef.current, {
        width: width,
        height: height,
        backgroundColor: canvasBackground
      });
      
      if (canvas.freeDrawingBrush) {
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush.color = brushColor;
        canvas.freeDrawingBrush.width = brushRadius;
      } else {
        const pencilBrush = new PencilBrush(canvas);
        canvas.freeDrawingBrush = pencilBrush;
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush.color = brushColor;
        canvas.freeDrawingBrush.width = brushRadius;
      }
      
      fabricCanvasRef.current = canvas;
      
      const handlePathCreated = () => {
        if (isStateUpdateAllowedRef.current) {
          saveCanvasState();
        }
      };
      
      canvas.on('path:created', handlePathCreated);
      
      setIsCanvasReady(true);
      hasInitializedRef.current = true;
      
      const handleResize = () => {
        if (!canvasRef.current || !fabricCanvasRef.current || !canvasContainerRef.current) return;
        
        const container = canvasContainerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;
        
        fabricCanvasRef.current.setWidth(width);
        fabricCanvasRef.current.setHeight(height);
        fabricCanvasRef.current.renderAll();
      };
      
      window.addEventListener('resize', handleResize);
      
      const timer = setTimeout(() => {
        if (fabricCanvasRef.current && !initialSaveCompletedRef.current) {
          isStateUpdateAllowedRef.current = true;
          saveCanvasState();
          initialSaveCompletedRef.current = true;
        }
      }, 800);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(timer);
        
        if (saveTimeoutRef.current !== null) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
        
        if (fabricCanvasRef.current) {
          fabricCanvasRef.current.off('path:created', handlePathCreated);
          fabricCanvasRef.current.dispose();
          fabricCanvasRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error initializing canvas:', error);
      hasInitializedRef.current = false;
      setIsCanvasReady(false);
    }
  }, [brushColor, brushRadius, canvasBackground]);
  
  useEffect(() => {
    if (!fabricCanvasRef.current || !isCanvasReady) return;
    
    try {
      fabricCanvasRef.current.backgroundColor = canvasBackground;
      fabricCanvasRef.current.renderAll();
    } catch (err) {
      console.error('Error updating canvas background:', err);
    }
  }, [canvasBackground, isCanvasReady]);
  
  useEffect(() => {
    if (!fabricCanvasRef.current || !isCanvasReady) return;
    
    try {
      if (!fabricCanvasRef.current.freeDrawingBrush) {
        fabricCanvasRef.current.isDrawingMode = true;
        const pencilBrush = new PencilBrush(fabricCanvasRef.current);
        fabricCanvasRef.current.freeDrawingBrush = pencilBrush;
      }
      
      if (fabricCanvasRef.current.freeDrawingBrush) {
        fabricCanvasRef.current.freeDrawingBrush.color = brushColor;
        fabricCanvasRef.current.freeDrawingBrush.width = brushRadius;
      }
    } catch (err) {
      console.error('Error updating brush:', err);
    }
  }, [brushColor, brushRadius, isCanvasReady]);
  
  const handleSave = () => {
    if (!fabricCanvasRef.current || !onSave) return;
    
    try {
      const dataUrl = fabricCanvasRef.current.toDataURL({
        format: 'png',
        multiplier: 1
      });
      
      onSave(dataUrl);
    } catch (error) {
      console.error('Error saving canvas:', error);
      toast({
        title: "Error saving canvas",
        description: "There was an error saving your drawing. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  const handleClear = () => {
    if (!fabricCanvasRef.current) return;
    
    try {
      fabricCanvasRef.current.clear();
      fabricCanvasRef.current.backgroundColor = canvasBackground;
      fabricCanvasRef.current.renderAll();
      saveCanvasState();
    } catch (error) {
      console.error('Error clearing canvas:', error);
    }
  };
  
  const handleUndo = () => {
    if (historyIndex <= 0 || !fabricCanvasRef.current) return;
    
    try {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      
      const canvasData = canvasHistory[newIndex];
      if (canvasData) {
        const img = document.createElement('img');
        img.src = canvasData;
        img.onload = () => {
          if (!fabricCanvasRef.current) return;
          
          fabricCanvasRef.current.clear();
          fabricCanvasRef.current.backgroundColor = canvasBackground;
          
          const ctx = fabricCanvasRef.current.getContext();
          if (ctx) {
            ctx.drawImage(img, 0, 0);
          }
          
          fabricCanvasRef.current.renderAll();
        };
      }
    } catch (error) {
      console.error('Error undoing canvas action:', error);
    }
  };
  
  const handleDownload = () => {
    if (!fabricCanvasRef.current) return;
    
    try {
      const dataUrl = fabricCanvasRef.current.toDataURL({
        format: 'png',
        multiplier: 1
      });
      
      const link = document.createElement('a');
      link.download = `drawing-${generateId()}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading canvas:', error);
      toast({
        title: "Error downloading",
        description: "There was an error downloading your drawing. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSaveApiKey = (apiKey: string) => {
    localStorage.setItem('openai_api_key', apiKey);
    handleGenerateAiBackground();
  };

  const handleContinueWithServerKey = () => {
    handleGenerateAiBackground();
  };

  const handleGenerateAiBackground = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Empty prompt",
        description: "Please enter a description for the background image",
        variant: "destructive"
      });
      return;
    }

    const apiKey = localStorage.getItem('openai_api_key');
    if (!apiKey) {
      setIsApiKeyDialogOpen(true);
      return;
    }

    setIsGeneratingImage(true);
    
    try {
      const imageUrl = await generateImage(aiPrompt);
      
      const imgElement = document.createElement('img');
      imgElement.crossOrigin = "anonymous";
      imgElement.src = imageUrl;
      
      imgElement.onload = () => {
        if (!fabricCanvasRef.current) return;
        
        const canvas = fabricCanvasRef.current;
        const canvasWidth = canvas.width || 800;
        const canvasHeight = canvas.height || 600;
        
        canvas.clear();
        
        const patternSourceCanvas = document.createElement('canvas');
        patternSourceCanvas.width = canvasWidth;
        patternSourceCanvas.height = canvasHeight;
        const patternCtx = patternSourceCanvas.getContext('2d');
        
        if (patternCtx) {
          patternCtx.drawImage(imgElement, 0, 0, canvasWidth, canvasHeight);
          
          const pattern = new Pattern({
            source: patternSourceCanvas,
            repeat: 'no-repeat'
          });
          
          canvas.backgroundColor = pattern;
          canvas.renderAll();
          saveCanvasState();
          
          toast({
            title: "Background generated",
            description: "The AI-generated background has been applied to your canvas"
          });
        }
      };
      
      imgElement.onerror = () => {
        toast({
          title: "Error loading image",
          description: "Could not load the generated image. Please try again.",
          variant: "destructive"
        });
        setIsGeneratingImage(false);
      };
      
      setIsAiDialogOpen(false);
    } catch (error) {
      console.error('Error generating AI image:', error);
      toast({
        title: "Failed to generate image",
        description: error instanceof Error ? error.message : "There was an error generating the background image",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };
  
  return (
    <div className="w-full h-full flex flex-col">
      <div className="bg-card p-4 flex flex-wrap items-center gap-2 border-b">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="w-8 h-8 p-0">
              <div
                className="w-5 h-5 rounded-full"
                style={{ backgroundColor: brushColor }}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="start">
            <div className="grid grid-cols-5 gap-2">
              {colors.map((color) => (
                <button
                  key={color}
                  className={`w-8 h-8 rounded-full border-2 ${
                    color === brushColor ? 'border-primary' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setBrushColor(color)}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
        
        <div className="flex items-center gap-2 flex-1">
          <Brush size={16} />
          <Slider
            className="w-32"
            min={1}
            max={20}
            step={1}
            value={[brushRadius]}
            onValueChange={(value) => setBrushRadius(value[0])}
          />
          <span className="text-xs">{brushRadius}px</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setIsAiDialogOpen(true)}
            className="gap-1"
            title="Generate AI background"
          >
            <Image size={16} />
            <span className="hidden sm:inline">Ask AI</span>
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleUndo}
            disabled={historyIndex <= 0}
          >
            <Undo size={16} />
          </Button>
          <Button variant="outline" size="icon" onClick={handleClear}>
            <Trash2 size={16} />
          </Button>
          <Button variant="outline" size="icon" onClick={handleDownload}>
            <Download size={16} />
          </Button>
          <Button onClick={handleSave} className="gap-1">
            <Send size={16} />
            Send
          </Button>
        </div>
      </div>
      
      <div 
        ref={canvasContainerRef} 
        className={`flex-1 overflow-hidden relative ${darkMode ? 'bg-[#222222]' : 'bg-white'}`}
      >
        <canvas ref={canvasRef} className="absolute inset-0" />
      </div>

      <Dialog open={isAiDialogOpen} onOpenChange={setIsAiDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate AI Background</DialogTitle>
            <DialogDescription>
              Enter a prompt to generate an AI background for your canvas using OpenAI's DALL-E
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="A serene mountain landscape with a lake..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              disabled={isGeneratingImage}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAiDialogOpen(false)}
              disabled={isGeneratingImage}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateAiBackground}
              disabled={isGeneratingImage || !aiPrompt.trim()}
            >
              {isGeneratingImage ? 'Generating...' : 'Generate Background'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApiKeyDialog
        open={isApiKeyDialogOpen}
        onOpenChange={setIsApiKeyDialogOpen}
        onContinue={handleContinueWithServerKey}
        onSave={handleSaveApiKey}
      />
    </div>
  );
};

export default DrawingCanvas;
